import React from 'react'
import type { Spot, Track } from '@nyos/shared'

/**
 * DestinationRail — the target track rendered as a drop zone: a rail
 * segment whose gauge shows current occupancy vs physical length, with
 * the pending cut overlaid as the projected segment. Spot targeting
 * (or free-form door #) lives inline beneath the rail.
 */

interface DestinationRailProps {
  tracks: Track[]
  /** Exclude the source track from destination options. */
  excludedTrackId?: string
  targetTrackId: string
  onSelectTrack: (trackId: string) => void
  spots: Spot[]
  targetSpotId: string
  onSelectSpot: (spotId: string) => void
  doorNumber: string
  onDoorChange: (door: string) => void
  /** Feet of the pending cut — drawn as the projected segment. */
  pendingCutFeet: number
  enabled: boolean
}

const DestinationRail: React.FC<DestinationRailProps> = ({
  tracks,
  excludedTrackId,
  targetTrackId,
  onSelectTrack,
  spots,
  targetSpotId,
  onSelectSpot,
  doorNumber,
  onDoorChange,
  pendingCutFeet,
  enabled,
}) => {
  const target = tracks.find((t) => t.id === targetTrackId)
  const physical = target?.totalPhysicalLengthFeet ?? 0
  // Capacity violations are against the clearance ceiling (usable
  // footage), not the rail end — PredictiveCapacityPanel parity.
  const clearance = target?.clearanceCapacityFeet ?? 0
  const occupied = target?.currentOccupiedFeet ?? 0
  const occupiedPct = physical > 0 ? Math.min(100, (occupied / physical) * 100) : 0
  const projectedPct =
    physical > 0 ? Math.min(100 - occupiedPct, (pendingCutFeet / physical) * 100) : 0
  const projectedTotal = occupied + pendingCutFeet
  const overflows = clearance > 0 && projectedTotal > clearance
  const openAfter = Math.max(0, clearance - projectedTotal)
  const overBy = Math.max(0, projectedTotal - clearance)
  const ceilingPct = physical > 0 ? (clearance / physical) * 100 : 0
  // Same severity ramp as PredictiveCapacityPanel.getProgressGradient,
  // applied to the projected total vs the clearance ceiling.
  const projectedFill = overflows
    ? 'bg-gradient-to-r from-orange-500 via-red-400 to-red-500'
    : projectedTotal <= clearance * 0.7
      ? 'bg-green-500'
      : projectedTotal <= clearance * 0.85
        ? 'bg-gradient-to-r from-green-500 via-green-400 to-yellow-500'
        : 'bg-gradient-to-r from-yellow-500 via-orange-400 to-orange-500'

  if (!enabled) {
    return (
      <section className="shrink-0 rounded-lg border border-subtle bg-card p-3 opacity-60">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
          Destination
        </div>
        <div className="text-xs text-muted-foreground">
          Anchor a cut on the source strip first — then pick where it lands.
        </div>
      </section>
    )
  }

  return (
    <section className="shrink-0 rounded-lg border border-subtle bg-card p-3 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Destination
        </div>
        <select
          value={targetTrackId}
          onChange={(e) => onSelectTrack(e.target.value)}
          className="px-3 py-1.5 border border-border rounded-md bg-background text-foreground text-xs min-w-[12rem]"
        >
          <option value="">Select destination track…</option>
          {tracks
            .filter((t) => t.id !== excludedTrackId)
            .map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.trackType ? ` (${t.trackType})` : ''}
              </option>
            ))}
        </select>
      </div>

      {target && (
        <>
          {/* capacity gauge: occupied + projected cut */}
          <div>
            <div
              className={`relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 ${
                overflows
                  ? 'animate-pulse shadow-[0_0_12px_#f43f5e] midnight:shadow-[0_0_12px_#dc2626] cyber:shadow-[0_0_12px_#dc2626] foundry:shadow-[0_0_12px_#dc2626] daylight:shadow-[0_0_12px_#f43f5e]'
                  : ''
              }`}
              title={`Physical ${physical.toLocaleString()} ft · clearance ceiling ${clearance.toLocaleString()} ft`}
            >
              <div className="h-4 rounded-full overflow-hidden flex">
                <div
                  className="h-full bg-railcar-fill transition-all duration-300"
                  style={{ width: `${occupiedPct}%` }}
                />
                <div
                  className={`h-full transition-all duration-300 ${projectedFill}`}
                  style={{ width: `${projectedPct}%` }}
                  title={`Pending cut: ${pendingCutFeet.toLocaleString()} ft`}
                />
              </div>
              {/* Clearance-ceiling tick — same rose marker as the inbound panel */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-rose-500 shadow-[0_0_8px_#f43f5e] z-10"
                style={{ left: `${ceilingPct}%` }}
              />
            </div>
            <div className="mt-1 flex justify-between text-xs font-mono text-muted-foreground">
              <span>
                {occupied.toLocaleString()} ft occupied
                {pendingCutFeet > 0 ? ` + ${pendingCutFeet.toLocaleString()} ft cut` : ''}
              </span>
              <span className={overflows ? 'text-destructive font-bold' : ''}>
                {pendingCutFeet > 0
                  ? `${projectedTotal.toLocaleString()}/${clearance.toLocaleString()} usable ft` +
                    (overflows
                      ? ` — exceeds clearance by ${overBy.toLocaleString()} ft`
                      : ` · ${openAfter.toLocaleString()} ft open`)
                  : `${clearance.toLocaleString()} usable ft · ${physical.toLocaleString()} ft rail`}
              </span>
            </div>
          </div>

          {/* spot targeting */}
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-wide text-muted-foreground whitespace-nowrap">
              {spots.length > 0 ? 'Spot' : 'Door # (optional)'}
            </span>
            {spots.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => onSelectSpot('')}
                  className={`px-2 py-1 rounded-md border text-xs ${
                    !targetSpotId
                      ? 'border-primary bg-primary/15 text-foreground'
                      : 'border-border text-muted-foreground'
                  }`}
                >
                  Any
                </button>
                {spots.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => onSelectSpot(s.id)}
                    className={`px-2 py-1 rounded-md border text-xs font-mono ${
                      targetSpotId === s.id
                        ? 'border-primary bg-primary/15 text-foreground'
                        : 'border-border text-muted-foreground'
                    }`}
                  >
                    {s.spotIdentifier ?? s.name}
                    {s.doorNumber ? ` · D${s.doorNumber}` : ''}
                  </button>
                ))}
              </div>
            ) : (
              <input
                value={doorNumber}
                onChange={(e) => onDoorChange(e.target.value)}
                placeholder="e.g. 2"
                className="w-24 px-2 py-1 border border-border rounded-md bg-background text-foreground text-xs"
              />
            )}
          </div>
        </>
      )}
    </section>
  )
}

export default React.memo(DestinationRail)
